import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { Loader2, FileText } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '../ui/button';
import ApplicationForm from './ApplicationForm';

const Candidates = ({ axiosInstance }) => {
  const [candidates, setCandidates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCandidate, setSelectedCandidate] = useState(null);

  useEffect(() => {
    const fetchCandidates = async () => {
      try {
        const api = axiosInstance || axios.create({
          baseURL: process.env.REACT_APP_BACKEND_URL + '/api'
        });
        
        // Add auth token if using default axios and token exists in localStorage
        if (!axiosInstance) {
          const token = localStorage.getItem('token');
          if (token) {
            api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          }
        }

        const response = await api.get('/hris/applications');
        setCandidates(response.data);
      } catch (error) {
        console.error('Error fetching candidates:', error);
        toast.error('Failed to load candidates');
      } finally {
        setIsLoading(false);
      }
    };

    fetchCandidates();
  }, [axiosInstance]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (selectedCandidate) {
    return (
      <ApplicationForm 
        initialData={selectedCandidate} 
        readOnly={true} 
        onBack={() => setSelectedCandidate(null)} 
        axiosInstance={axiosInstance} 
      />
    );
  }

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'Hired': return 'success'; // Assuming success variant exists or use default styles
      case 'Rejected': return 'destructive';
      case 'Pending': return 'secondary';
      default: return 'outline';
    }
  };

  const getStatusColor = (status) => {
     switch (status) {
      case 'Hired': return 'bg-green-100 text-green-800 hover:bg-green-100';
      case 'Rejected': return 'bg-red-100 text-red-800 hover:bg-red-100';
      case 'Pending': return 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100';
      default: return 'bg-gray-100 text-gray-800 hover:bg-gray-100';
    }
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Candidates</h2>
          <p className="text-muted-foreground">Manage job applications and candidate status.</p>
        </div>
        <div className="text-sm font-medium bg-blue-50 text-blue-700 px-3 py-1 rounded-full">
          Total: {candidates.length}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Job Applications</CardTitle>
        </CardHeader>
        <CardContent>
          {candidates.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium">No applications found</p>
              <p className="text-sm">New job applications will appear here.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Applicant Name</TableHead>
                  <TableHead>Position Applied</TableHead>
                  <TableHead>Date Applied</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {candidates.map((candidate) => (
                  <TableRow key={candidate.id}>
                    <TableCell className="font-medium">
                      {candidate.personal_info.last_name}, {candidate.personal_info.first_name}
                    </TableCell>
                    <TableCell>{candidate.position_applied}</TableCell>
                    <TableCell>
                      {new Date(candidate.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Badge className={getStatusColor(candidate.status)} variant="outline">
                        {candidate.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                       <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setSelectedCandidate(candidate)}
                      >
                        View Details
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Candidates;
